import { zonedWallClockToUtc } from "./pass-status";

/**
 * A date-time with NO zone designator: "2026-09-22T13:15", seconds optional,
 * and the time optional too ("2026-09-22" is midnight on that day). This is
 * how an authored time travels on the wire — the digits a picker showed,
 * nothing more.
 */
const WALL_CLOCK_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/;

/** True for an authored wall clock, false for an instant (Z or ±HH:MM) and
 *  for anything that is not a date-time at all. */
export function isWallClock(value: string): boolean {
  return WALL_CLOCK_RE.test(value);
}

function pad(n: number, width = 2): string {
  return String(n).padStart(width, "0");
}

/**
 * The wall clock a date picker is showing, as a string — read off the Date's
 * OWN components (`getFullYear`, `getHours`, …), never through `Intl`.
 *
 * That is the whole point: a client's IANA table may be stale (a browser on
 * tzdata 2026b in a Morocco that moved to UTC+0 on 2026-09-20), but its
 * ability to report the digits it just rendered never is. The zone those
 * digits are read in is the organization's, and only the server applies it —
 * see `authoredInstant`.
 */
export function wallClockOf(d: Date): string {
  return wallClockFromParts(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    d.getHours(),
    d.getMinutes(),
  );
}

/** `wallClockOf` from loose components — `month` is 0-based, like Date. For
 *  callers holding a calendar day and a time-of-day rather than a Date. */
export function wallClockFromParts(
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number,
): string {
  // Normalising through Date.UTC lets callers pass a day past the end of the
  // month (`day + 1` on the 31st) or minutes past midnight, exactly as
  // `zonedWallClockToUtc` does with the same arithmetic.
  const normalized = new Date(Date.UTC(year, month, day, hours, minutes));
  return (
    `${pad(normalized.getUTCFullYear(), 4)}-${pad(normalized.getUTCMonth() + 1)}-${pad(normalized.getUTCDate())}` +
    `T${pad(normalized.getUTCHours())}:${pad(normalized.getUTCMinutes())}`
  );
}

/**
 * The instant an authored value denotes, resolved on the ORGANIZATION's clock.
 *
 * Two shapes are accepted, and the difference is not cosmetic:
 *
 * - a **wall clock** ("2026-09-22T13:15") is anchored with
 *   `zonedWallClockToUtc` in `timeZone`. This is what clients send, and it is
 *   the only shape that survives a client whose tz table is behind: the
 *   conversion happens here, in the one runtime we can keep current.
 * - an **instant** ("…Z", "…+01:00") is taken at face value. Builds of the
 *   mobile app in the wild still compute their own, and an instant already
 *   means something exact — reinterpreting it would be inventing data.
 *
 * Throws on anything else rather than handing back an Invalid Date, whose NaN
 * would otherwise reach a timestamp column. Callers at a request boundary
 * translate that into a 400.
 */
export function authoredInstant(value: string, timeZone: string): Date {
  const m = WALL_CLOCK_RE.exec(value);
  if (m) {
    const [, year, month, day, hours, minutes, seconds] = m;
    const at = zonedWallClockToUtc(
      Number(year),
      Number(month) - 1,
      Number(day),
      // A bare date is midnight ON THE ORG'S CLOCK. `new Date("2026-09-22")`
      // would have made it UTC midnight — the previous evening anywhere east
      // of Greenwich, which is the whole class of bug this module exists to
      // end.
      hours ? Number(hours) : 0,
      minutes ? Number(minutes) : 0,
      timeZone,
    );
    // Seconds ride along unshifted: no zone offset has ever had a sub-minute
    // component in the IANA database's modern era, so anchoring the minute
    // and adding the seconds back is exact.
    return seconds ? new Date(at.getTime() + Number(seconds) * 1000) : at;
  }
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) {
    throw new Error(`unparseable authored time: ${JSON.stringify(value)}`);
  }
  return instant;
}
